import React from 'react';
import { catNorm } from '../utils/searchNorm';

/**
 * Wrap the parts of `text` that match any token of `query` in <mark>, using the same folding as the
 * catalog search (Arabic letter shapes, digits, diacritics, spaces and hyphens ignored). Matching is
 * done on the folded string and mapped back to the original characters, so "ازم" highlights "أزمير"
 * and "f s sat" highlights "F-S-SAT".
 */
export function highlightText(text: string | null | undefined, query: string | null | undefined): React.ReactNode {
  if (!text) return text ?? null;
  const tokens = (query || '').split(/\s+/).map(catNorm).filter(Boolean);
  if (tokens.length === 0) return text;

  const chars = Array.from(text);
  const folded = chars.map((c) => catNorm(c));
  const hit = new Array<boolean>(chars.length).fill(false);

  for (const t of tokens) {
    for (let i = 0; i < chars.length; i++) {
      if (!folded[i]) continue; // a char that folds to nothing (space, hyphen) cannot start a match
      let s = '';
      let j = i;
      while (j < chars.length && s.length < t.length) {
        s += folded[j];
        j++;
      }
      if (s === t) for (let k = i; k < j; k++) hit[k] = true;
    }
  }
  if (!hit.some(Boolean)) return text;

  const out: React.ReactNode[] = [];
  let buf = '';
  let inMark = false;
  const flush = () => {
    if (!buf) return;
    out.push(inMark ? <mark key={out.length} style={markStyle}>{buf}</mark> : <React.Fragment key={out.length}>{buf}</React.Fragment>);
    buf = '';
  };
  chars.forEach((c, i) => {
    if (hit[i] !== inMark) {
      flush();
      inMark = hit[i];
    }
    buf += c;
  });
  flush();
  return <>{out}</>;
}

const markStyle: React.CSSProperties = {
  background: '#fff1a8',
  color: 'inherit',
  padding: 0,
  borderRadius: 2,
};

/** Convenience component: <Highlight text={...} query={...} /> */
const Highlight: React.FC<{ text?: string | null; query?: string | null }> = ({ text, query }) => (
  <>{highlightText(text, query)}</>
);

export default Highlight;
